import { safe } from '@w/utils'
import type { Block, Blocks, BlockType, ExtractBlock, PageRef } from './block'
import { isBlockLevelBlock } from './block'

// oxlint-disable-next-line typescript/no-explicit-any -- needed for generic typing
type a = any

interface InfoOps {
  stop: () => void
  getParent: () => Block
  getParents: () => Blocks
  getBlockLevelParent: () => Block
  getPath: () => string
  order: number
  i: number
}

interface Ops extends InfoOps {
  remove: () => void
  replace: (block: Block | Blocks) => void
}

type CBT<T extends Block['type']> = (block: ExtractBlock<T>, ops: Ops) => void
type CB = (block: Block, ops: Ops) => void

export function traverseBlocks<T extends Block['type']>(
  blocks: Blocks,
  type: T,
  cb: CBT<T>,
): Blocks
export function traverseBlocks(blocks: Blocks, cb: CB): Blocks

/** Breadth-first traversal; callbacks may remove or replace blocks.
 * Each child list is copied before visiting it, preserving its queued visits. */
export function traverseBlocks<T extends BlockType>(
  blocks: Blocks,
  cbOrType: a,
  cb?: a,
) {
  const isCB = typeof cbOrType === 'function'
  if (!isCB && typeof cb !== 'function')
    throw new Error('cbOrType must be a function')
  const type = isCB ? undefined : (cbOrType as T)

  const callback = isCB ? (cbOrType as CB) : (cb as CBT<T>)

  const queue: [Block, Blocks][] = [
    [{ id: '', type: 'root', children: blocks }, [...blocks]],
  ] // copy children to remove or add elements in parent.children while traversing
  const blockAndParent = new Map<Block, Block>() // ids are not unique

  let order = -1
  while (queue.length > 0) {
    const [parent, children] = safe(queue.shift())

    for (let i = 0; i < children.length; i++) {
      const child = children[i]

      order++

      blockAndParent.set(child, parent)

      if ('children' in child) queue.push([child, [...(child.children ?? [])]])

      const ops: Ops = {
        remove: () => {
          if (!('children' in parent && parent.children))
            throw new Error('parent has no children')
          const i = parent.children.indexOf(child as a)
          parent.children.splice(i, 1)
        },
        replace: (block) => {
          if (!('children' in parent && parent.children))
            throw new Error('parent has no children')
          const i = parent.children.indexOf(child as a)
          parent.children.splice(
            i,
            1,
            ...(Array.isArray(block) ? block : [block]),
          )
        },
        stop: () => {
          queue.length = 0
          i = children.length
        },
        getBlockLevelParent: () => {
          let it = 0
          let parentRef = parent
          while (!isBlockLevelBlock(parentRef.type) && it++ < 10e4) {
            parentRef = blockAndParent.get(parentRef) as Block
            if (!parentRef) throw new Error('no block level parent')
          }
          if (it >= 10e4)
            throw new Error('traverseBlocks endless loop getBlockLevelParent')
          return parentRef
        },
        getParents: () => {
          let it = 0
          const parents = [parent]
          let parentRef = parent
          while (parentRef.type !== 'root' && it++ < 10e4) {
            parentRef = blockAndParent.get(parentRef) as Block
            parents.push(parentRef)
          }
          if (it >= 10e4)
            throw new Error('traverseBlocks endless loop getParents')
          return parents
        },
        getParent: () => parent,
        getPath: () => {
          let it = 0
          let path = parent.id
          let parentRef = parent
          let grandParent = blockAndParent.get(parentRef)
          while (grandParent && grandParent.type !== 'root' && it++ < 10e4) {
            parentRef = grandParent
            path = `${parentRef.id ? `${parentRef.id}.` : ''}${path}`
            grandParent = blockAndParent.get(parentRef)
          }
          if (it >= 10e4) throw new Error('traverseBlocks endless loop getPath')
          return `${path ? `${path}.` : ''}${child.id}`
        },
        order,
        i,
      }

      if (type && child.type === type) callback(child as ExtractBlock<T>, ops)
      else if (!type) callback(child as ExtractBlock<T>, ops)
    }
  }
  return blocks
}

type DepthFirstCB<T extends Block = Block> = (
  block: T,
  ops: Pick<Ops, 'getParent'>,
) => void

export function traverseBlocksDepthFirst<T extends Block['type']>(
  blocks: Blocks,
  type: T,
  cb: DepthFirstCB<ExtractBlock<T>>,
): Blocks
export function traverseBlocksDepthFirst(blocks: Blocks, cb: DepthFirstCB): Blocks
export function traverseBlocksDepthFirst<T extends BlockType>(
  blocks: Blocks,
  cbOrType: a,
  cb?: a,
) {
  const isCB = typeof cbOrType === 'function'
  if (!isCB && typeof cb !== 'function')
    throw new Error('cbOrType must be a function')
  const type = isCB ? undefined : (cbOrType as T)
  const callback = isCB
    ? (cbOrType as DepthFirstCB)
    : (cb as DepthFirstCB<ExtractBlock<T>>)

  const traverse = (blocks: Blocks, parent: Block) => {
    blocks.forEach((block) => {
      if (!type || block.type === type) {
        callback(block as ExtractBlock<T>, { getParent: () => parent })
      }
      if ('children' in block && block.children) {
        traverse(block.children, block)
      }
    })
  }

  traverse(blocks, { id: '', type: 'root', children: blocks })
  return blocks
}

export function filterBlocks(
  blocks: Blocks | undefined,
  predicate: (block: Block, ops: Ops) => boolean,
): Blocks
export function filterBlocks<T extends BlockType>(
  blocks: Blocks | undefined,
  type: T,
  predicate?: (block: ExtractBlock<T>, ops: Ops) => boolean,
): ExtractBlock<T>[]

export function filterBlocks(
  blocks: Blocks | undefined,
  typeOrPredicate: a,
  predicate?: (block: Block, ops: Ops) => boolean,
) {
  if (!blocks) return []
  const isType = typeof typeOrPredicate === 'string'
  const type = isType ? typeOrPredicate : undefined
  const cb = isType ? predicate : typeOrPredicate

  const r: Blocks = []
  traverseBlocks(blocks, (block, ops) => {
    if (type && block.type !== type) return
    if (!cb || cb(block, ops)) r.push(block)
  })

  return r
}

export function findBlock(
  blocks: Blocks,
  cb: (block: Block, ops: Ops) => boolean,
): Block | undefined
export function findBlock<T extends BlockType>(
  blocks: Blocks,
  type: T,
  predicate?: (block: ExtractBlock<T>, ops: Ops) => boolean | undefined,
): ExtractBlock<T> | undefined

export function findBlock<T extends BlockType>(
  blocks: Blocks,
  typeOrPredicate: a,
  predicate?: (block: ExtractBlock<T>, ops: Ops) => boolean | undefined,
): Block | undefined {
  const isType = typeof typeOrPredicate === 'string'
  const type = isType ? (typeOrPredicate as T) : undefined
  const callback = isType ? predicate : typeOrPredicate

  let r: Block | undefined
  traverseBlocks(blocks, type as T, (block, ops) => {
    if (!callback || callback(block, ops)) {
      r = block
      ops.stop()
    }
  })
  return r
}

/** Reject non-local structural coordinates and malformed page references. */
export function assertLocalStructuralReferences(value: unknown): void {
  if (Array.isArray(value)) {
    for (const child of value) assertLocalStructuralReferences(child)
  } else if (value && typeof value === 'object') {
    const block = value as Record<string, unknown>
    if ((block.type === 'page' || block.type === 'pages') && 'spaceId' in block) throw new Error('Structural page references must be local')
    if ((block.type === 'pageLink' || block.type === 'page') && typeof block.pageId === 'string' &&
      block.spaceId !== undefined && (typeof block.spaceId !== 'string' || !block.spaceId)) throw new Error('Invalid page link spaceId')
    if (block.type === 'pages' && Array.isArray(block.ids)) {
      for (const pageId of block.ids) {
        if (typeof pageId !== 'string') throw new Error('Invalid structural page reference')
      }
    }
    for (const child of Object.values(block)) assertLocalStructuralReferences(child)
  }
}

/** Read every nested reference, including captions, questions and option arrays. */
export function visitPageReferences(value: unknown, containingSpaceId: string, visit: (ref: PageRef, structural: boolean) => void): void {
  assertLocalStructuralReferences(value)
  visitReferences(value)

  function visitReferences(value: unknown): void {
    if (Array.isArray(value)) {
      for (const child of value) visitReferences(child)
    } else if (value && typeof value === 'object') {
      const block = value as Record<string, unknown>
      if ((block.type === 'pageLink' || block.type === 'page') && typeof block.pageId === 'string') {
        visit({ spaceId: typeof block.spaceId === 'string' ? block.spaceId : containingSpaceId, pageId: block.pageId }, block.type === 'page')
      }
      if (block.type === 'pages' && Array.isArray(block.ids)) {
        for (const pageId of block.ids) visit({ spaceId: containingSpaceId, pageId }, true)
      }
      for (const child of Object.values(block)) visitReferences(child)
    }
  }
}
